// SentimentWidget — global LONG/SHORT mix con ventana móvil 4h/8h/24h.
// Public (no auth). Refresh sincronizado a slot boundaries 4h UTC.
import React, { useCallback, useState } from "react";
import { API } from "../../lib/api";
import { useSlotBoundary } from "../../hooks/useSlotBoundary";

const TXT = {
  title: { es: "Sentimiento Shadow", en: "Shadow Sentiment", sk: "Shadow Sentiment", de: "Shadow-Stimmung", "zh-CN": "Shadow 情绪" },
  bullish: { es: "ALCISTA", en: "BULLISH", sk: "BÝČÍ", de: "BULLISCH", "zh-CN": "看涨" },
  bearish: { es: "BAJISTA", en: "BEARISH", sk: "MEDVEDÍ", de: "BÄRISCH", "zh-CN": "看跌" },
  neutral: { es: "NEUTRAL", en: "NEUTRAL", sk: "NEUTRÁLNE", de: "NEUTRAL", "zh-CN": "中性" },
  basedOn: {
    es: (n, w) => `Basado en ${n} análisis · últimas ${w}h`,
    en: (n, w) => `Based on ${n} analyses · last ${w}h`,
    sk: (n, w) => `Na základe ${n} analýz · posledných ${w}h`,
  },
  empty: {
    es: "Aún no hay datos recientes",
    en: "No recent data yet",
    sk: "Zatiaľ žiadne dáta", de: "Noch keine aktuellen Daten vorhanden", "zh-CN": "暂无最新数据",
  },
  windowBadge: {
    es: (w) => `Últimas ${w}h`,
    en: (w) => `Last ${w}h`,
    sk: (w) => `Posledných ${w}h`,
  },
};

export const SentimentWidget = ({ lang = "es" }) => {
  const L = ["es", "en", "sk", "de", "zh-CN"].includes(lang) ? lang : "en";
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/shadow/sentiment/today`);
      const j = await r.json();
      setData(j);
    } catch (_e) {
      // silent — widget is non-critical
    }
  }, []);

  // Refresh sincronizado a boundaries de 4h UTC — mismo tick que el Brief IA.
  useSlotBoundary(load);

  if (!data) return null;
  const total = data.total || 0;
  const clamp = (n) => Math.max(0, Math.min(100, Number(n) || 0));
  const bull = clamp(data.bullish);
  const bear = clamp(data.bearish);
  const neut = clamp(data.neutral);

  return (
    <div
      data-testid="shadow-sentiment-widget"
      className="rounded-xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur"
    >
      <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-white/55">
          {TXT.title[L]}
        </span>
        <div className="flex items-center gap-2">
          {data.window_hours && total > 0 && (
            <span
              className="rounded-full border border-white/10 bg-white/[0.04] px-1.5 py-0.5 font-mono text-[9px] text-white/50"
              data-testid="sentiment-window-badge"
            >
              {TXT.windowBadge[L](data.window_hours)}
            </span>
          )}
          <span className="font-mono text-[10px] text-white/35">
            {total > 0 ? TXT.basedOn[L](total, data.window_hours || 24) : TXT.empty[L]}
          </span>
        </div>
      </div>

      {total > 0 ? (
        <>
          <div className="flex h-2.5 overflow-hidden rounded-full bg-white/5">
            <div
              className="bg-emerald-400/85 transition-all duration-700"
              style={{ width: `${bull}%` }}
              data-testid="sentiment-bar-bullish"
            />
            <div
              className="bg-white/15 transition-all duration-700"
              style={{ width: `${neut}%` }}
              data-testid="sentiment-bar-neutral"
            />
            <div
              className="bg-red-400/85 transition-all duration-700"
              style={{ width: `${bear}%` }}
              data-testid="sentiment-bar-bearish"
            />
          </div>
          <div className="mt-2.5 flex items-center justify-between font-mono text-[11px]">
            <span className="text-emerald-300" data-testid="sentiment-pct-bullish">
              🟢 {bull}% {TXT.bullish[L]}
            </span>
            {neut > 0 && (
              <span className="text-white/40" data-testid="sentiment-pct-neutral">
                ⚪ {neut}% {TXT.neutral[L]}
              </span>
            )}
            <span className="text-red-300" data-testid="sentiment-pct-bearish">
              🔴 {bear}% {TXT.bearish[L]}
            </span>
          </div>
        </>
      ) : (
        <div className="py-2 text-center text-[11px] text-white/35">{TXT.empty[L]}</div>
      )}
    </div>
  );
};

export default SentimentWidget;
